import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft, FaSlidersH } from "react-icons/fa";
import Navbar from "../../layouts/frontend/Navbar";
import Footer from "../../layouts/frontend/Footer";
import SectionRenderer from "../../themes/SectionRenderer";
import { themeButtonCssVars } from "../../themes/themeRegistry";
import { buildThemeDemoCatalog, prepareThemeForDemo } from "../../themes/themeDemoData";
import { loadStoreSettings } from "../../util/storeSettings";
import "../../assets/frontend/styles/frontsyle.css";
import { loadDesignStyle } from "../../themes/designStyles";
import "../../assets/frontend/styles/theme-demo-page.css";

const FALLBACK_DEMO_STORE = { storeName: "Your Store", logoUrl: "" };

export default function ThemeDemoPage() {
  const { themeKey } = useParams();
  const location = useLocation();
  const embedded = new URLSearchParams(location.search).get("embedded") === "1";
  // The editor's Preview opens the saved draft; the theme library shows the published theme.
  const draft = new URLSearchParams(location.search).get("draft") === "1";
  const [theme, setTheme] = useState(null);
  const [demoStore, setDemoStore] = useState(FALLBACK_DEMO_STORE);
  const [error, setError] = useState(false);
  const catalog = useMemo(() => buildThemeDemoCatalog(themeKey), [themeKey]);
  const customizeUrl = `/admin/theme-editor?theme=${themeKey}&content=demo`;

  const openCustomizer = (event) => {
    if (typeof window === "undefined" || !window.opener || window.opener.closed) return;
    try {
      if (window.opener.location.origin !== window.location.origin) return;
      event.preventDefault();
      window.opener.location.assign(customizeUrl);
      window.opener.focus();
      window.close();
    } catch {
      // If browser opener access is unavailable, the link continues normally.
    }
  };

  useEffect(() => {
    let mounted = true;
    setTheme(null);
    setError(false);
    axios.get(`/api/themes/${encodeURIComponent(themeKey)}?draft=${draft ? 1 : 0}`)
      .then((res) => {
        if (!mounted) return;
        const loaded = res.data?.theme;
        if (!loaded) throw new Error("Theme not found");
        loadDesignStyle(loaded.settings?.designStyle);
        setTheme(prepareThemeForDemo(loaded, themeKey));
        document.title = `${loaded.name || themeKey} demo`;
      })
      .catch(() => mounted && setError(true));
    return () => { mounted = false; };
  }, [themeKey, draft]);

  useEffect(() => {
    let mounted = true;
    loadStoreSettings()
      .then((settings) => {
        if (!mounted || !settings) return;
        setDemoStore({
          ...settings,
          storeName: String(settings.storeName || settings.store_name || "").trim() || FALLBACK_DEMO_STORE.storeName,
        });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (error) {
    return <div className="theme-demo-page-state"><h1>Theme demo unavailable</h1><Link to="/admin/themes">Return to themes</Link></div>;
  }

  if (!theme) {
    return <div className="theme-demo-page-state"><span className="theme-demo-page-spinner" />Loading real theme demo…</div>;
  }

  const settings = { ...(theme.settings || {}), showFooterLogo: false };
  const cssVars = {
    "--theme-secondary": settings.secondaryColor || "#0f172a",
    "--theme-font": settings.fontFamily || "Georgia, serif",
    "--theme-body-font": settings.bodyFontFamily || "Inter, sans-serif",
    ...themeButtonCssVars(settings),
  };

  return (
    <div className={`theme-demo-page ${embedded ? "is-embedded" : ""}`}>
      {!embedded && (
        <div className="theme-demo-page-bottom-bar">
          <Link className="theme-demo-page-back" to="/admin/themes" aria-label="Return to themes">
            <FaArrowLeft /> Themes
          </Link>
          <span>Previewing <strong>{theme.name}</strong>{draft ? " · saved draft" : ""}</span>
          <Link className="theme-demo-page-customize" to={customizeUrl} onClick={openCustomizer}>
            <FaSlidersH /> Customize theme
          </Link>
        </div>
      )}
      <div className="storefront-shell d-flex flex-column min-vh-100" data-design={settings.designStyle || undefined} style={cssVars}>
        <Navbar themeSettings={settings} storeSettingsOverride={demoStore} />
        <main className="main-content">
          <SectionRenderer
            sections={theme.sections || []}
            themeKey={theme.theme_key || themeKey}
            globalSettings={settings}
            categories={catalog.categories}
            products={catalog.products}
            banners={[]}
            storeSettings={demoStore}
            productsReady
            isEditor={false}
          />
        </main>
        <Footer themeSettings={settings} embedded storeSettingsOverride={demoStore} />
      </div>
    </div>
  );
}
